import React, { useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import { Card } from './Card';

const openModals = [];

const isInteractiveElement = (target) => (
  target instanceof Element
  && Boolean(target.closest('button, a, input, select, textarea, [contenteditable="true"]'))
);

const Modal = ({
  isOpen,
  onClose,
  onEnter,
  children,
  className = '',
  zIndex = 'z-50',
}) => {
  const modalId = useRef(Symbol('modal'));
  const onCloseRef = useRef(onClose);
  const onEnterRef = useRef(onEnter);

  onCloseRef.current = onClose;
  onEnterRef.current = onEnter;

  useEffect(() => {
    if (!isOpen) return undefined;

    const id = modalId.current;
    openModals.push(id);

    const handleKeyDown = (event) => {
      if (openModals[openModals.length - 1] !== id || event.defaultPrevented || event.repeat) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        onCloseRef.current?.();
      } else if (event.key === 'Enter' && onEnterRef.current && !isInteractiveElement(event.target)) {
        event.preventDefault();
        onEnterRef.current();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      const index = openModals.lastIndexOf(id);
      if (index !== -1) openModals.splice(index, 1);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div
      className={`fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center ${zIndex} p-4`}
      onClick={onClose}
      role="presentation"
    >
      <Card
        className={`relative shadow-lg ${className}`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-4 right-4 z-10 text-gray-400 hover:text-gray-600 transition-colors"
          aria-label="Close"
        >
          <X className="h-5 w-5" />
        </button>
        {children}
      </Card>
    </div>
  );
};

export default Modal;
